<script module lang="ts">
	import type { Component, ComponentProps, Snippet, SvelteComponent } from "svelte";

	export interface Action {
		text: string;
		color?: "red" | "yellow" | "green" | "purple" | "blue" | "light" | "dark" | "primary" | "none" | "alternative";
		disabled?: boolean;
		onClick: (e: Event) => void;
	}

	export interface Options<Props extends Record<string, unknown> = Record<string, unknown>> {
		title?: string;
		backButtonTitle?: string;
		backButtonSnippet?: Snippet<[Options, PopFn]>;
		props?: Partial<Props>;
		actions?: Action[];
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	export type PushFn = <TComponent extends Component<any>>(component: TComponent | Snippet<[Options]>, options?: Options<ComponentProps<TComponent>>) => void;

	export type PopFn = (back?: boolean, root?: boolean) => void;

	export type CloseFn = () => void;

	interface StackItem {
		component: Component;
		options?: Options;
	}

	type MaybePromise<T> = Promise<T> | T;
</script>

<script lang="ts">
	import { Heading } from "flowbite-svelte-v1";
	import { setContext } from "svelte";
	import { cubicOut } from "svelte/easing";
	import { fade, fly } from "svelte/transition";
	import { twMerge } from "tailwind-merge";
	import Icon from "#lib/components/Icon.svelte";
	import type { ViewerComponent } from "#lib/components/flow/viewer/SuiteViewer.svelte";
	import Button from "#lib/ui/Button/index.js";

	interface Props extends Record<string, unknown> {
		rootComponent?: Component;
		topController?: ViewerComponent | SvelteComponent;
		closable?: boolean;
		back?: boolean;
		// bare drops the header space, the body gutter, and the stacked item's opaque
		// background so tray content can paint edge to edge. Defaults off so other
		// StackController consumers are unaffected.
		bare?: boolean;
		closeColor?: string;
		closeIconColor?: string;
		closeBorderColor?: string;
		titleClass?: string;
		scrollContainerClass?: string;
		leftClass?: string;
		rightClass?: string;
		bodyClass?: string;
		footerClass?: string;
		dirty?: boolean;
		left?: Snippet<[string | undefined, PopFn]>;
		center?: Snippet<[string | undefined]>;
		right?: Snippet<[CloseFn]>;
		children?: Snippet;
		footer?: Snippet<[number]>;
		onClose?: CloseFn;
		onNext?: (component: Component, options?: Options) => MaybePromise<boolean>;
		onPrevious?: (component: Component, options?: Options, back?: boolean) => MaybePromise<boolean>;
	}

	let { rootComponent, topController = $bindable(), closable = true, back = true, bare = false, closeColor, closeIconColor, closeBorderColor, titleClass = "", scrollContainerClass = "", bodyClass = "", footerClass = "", leftClass = "", rightClass = "", dirty = $bindable(false), left, center, right, children, footer, onClose, onNext, onPrevious, ...rest }: Props = $props();

	let scrollContainer: HTMLDivElement | undefined = $state();

	// svelte-ignore state_referenced_locally
	let stack: StackItem[] = $state(rootComponent ? [{ component: rootComponent, options: { props: rest as Record<string, unknown> } }] : []);

	const showTitleBar = $derived(stack.length > (children ? 0 : 1));
	const flyTransition = $derived({ x: scrollContainer?.offsetWidth || 0, duration: 250, opacity: 100, easing: cubicOut });

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	export async function push<TComponent extends Component<any>>(component: TComponent | Snippet<[Options]>, options?: Options<ComponentProps<TComponent>>) {
		const normalizedComponent = component as Component;
		if (onNext && !(await onNext(normalizedComponent, options))) {
			return;
		}
		const newStack = stack;
		newStack.push({ component: normalizedComponent, options: options });
		resetScroll();
		stack = newStack;
	}

	export async function popToRoot() {
		await pop(false, true);
	}

	export async function pop(back: boolean = false, root: boolean = false) {
		const top = getStackTop();
		if (!top) {
			return;
		}
		if (onPrevious && !(await onPrevious(top.component, top.options, back))) {
			return;
		}
		let newStack = stack;
		if (root) {
			newStack = [];
		} else {
			newStack.splice(newStack.length - 1, 1);
		}
		resetScroll();
		stack = newStack;
	}

	function close() {
		onClose?.();
	}

	function resetScroll() {
		if (!scrollContainer || scrollContainer.scrollTop === 0) {
			return;
		}
		scrollContainer.scrollTop = 0;
	}

	function getStackTop(): StackItem | undefined {
		if (stack.length == 0) {
			return;
		}
		return stack[stack.length - 1];
	}

	function getCurrentOptions(): Options {
		return getStackTop()?.options ?? {};
	}

	function handleTransitionStart(e: Event) {
		const target = e.target as HTMLElement;
		target.classList.remove("hidden");
		target.classList.add("absolute", "left-0", "right-0", "top-0", "z-99");
	}

	function handleTransitionEnd(e: Event) {
		const target = e.target as HTMLElement;
		target.classList.remove("absolute", "left-0", "right-0", "top-0", "z-99");
		topController?.onIntroEnd?.();
	}

	function isSnippet(component: unknown): component is Snippet<[Options]> {
		return typeof component === "function" && component.length === 1;
	}

	setContext<PushFn>("push", push);
	setContext<PopFn>("pop", pop);
</script>

<div class="flex h-full min-h-0 w-full flex-col items-stretch">
	{#if closable || showTitleBar}
		{@const options = getCurrentOptions()}
		<div class={twMerge("top-0 z-10 flex flex-none items-center justify-between gap-2 px-4 py-2", bare && "items-start pt-0 pb-4", titleClass)} in:fade={{ duration: 200 }}>
			<div class={twMerge("flex-none", leftClass)}>
				{#if options.backButtonSnippet}
					{@render options.backButtonSnippet?.(options, pop)}
				{:else if back && showTitleBar}
					<Button data-action-name="stack-controller-back" data-action-title="Back" color="none" class="flex gap-2 px-0 py-0" on:click={() => pop(true)}>
						<Icon name="arrow-left" class="h-6 w-6" />
						<div>{options.backButtonTitle ?? "Back"}</div>
					</Button>
				{:else}
					{@render left?.(options.backButtonTitle, pop)}
				{/if}
			</div>
			<div class="grow text-left">
				{#if center}
					{@render center?.(options.title)}
				{:else}
					<Heading tag="h6">{options.title ?? ""}</Heading>
				{/if}
			</div>
			<div class={twMerge("flex flex-none gap-2", rightClass)}>
				{#if closable}
					<Button data-action-name="stack-controller-close" data-action-title="Close" color="none" class={twMerge("p-0 text-right", bare && "rounded-full border p-1.5")} style={bare ? `background-color: ${closeColor ?? "transparent"}; border-color: ${closeBorderColor ?? "currentColor"};` : closeColor ? `background-color: ${closeColor};` : undefined} on:click={close}>
						<Icon name="x-mark" style={closeIconColor ? `color: ${closeIconColor};` : undefined} />
						<span class="sr-only">Close</span>
					</Button>
				{:else if right}
					{@render right?.(close)}
				{:else}
					{#each options.actions ?? [] as action, index (index)}
						<Button data-action-name="stack-controller-action" data-action-title={action.text} disabled={action.disabled} color={action.color} on:click={action.onClick}>
							{action.text}
						</Button>
					{/each}
				{/if}
			</div>
		</div>
	{/if}
	<div bind:this={scrollContainer} class={twMerge("flex h-full min-h-0 w-full grow flex-col overflow-y-auto px-4", scrollContainerClass)}>
		<div class={twMerge("relative flex grow flex-col", bodyClass)}>
			{#each stack as item, index (index)}
				<div class={twMerge("flex h-full grow flex-col items-stretch bg-white", bare && "bg-transparent")} class:hidden={index !== stack.length - 1} transition:fly={flyTransition} onoutrostart={handleTransitionStart} onintrostart={handleTransitionStart} onoutroend={handleTransitionEnd} onintroend={handleTransitionEnd}>
					{#if isSnippet(item.component)}
						{@render item.component(item.options ?? {})}
					{:else}
						<item.component bind:this={topController} {...item.options?.props} bind:dirty />
					{/if}
				</div>
			{/each}
			{#if children}
				<div class={twMerge("flex h-full grow flex-col items-stretch bg-white", bare && "bg-transparent")} class:hidden={stack.length > 0} transition:fly={flyTransition} onoutrostart={handleTransitionStart} onintrostart={handleTransitionStart} onoutroend={handleTransitionEnd} onintroend={handleTransitionEnd}>
					{@render children?.()}
				</div>
			{/if}
		</div>
	</div>
	{#if footer}
		<div class={twMerge("w-full flex-none px-4", footerClass)}>
			{@render footer?.(stack.length)}
		</div>
	{/if}
</div>
